import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useNavigation } from "../NavigationProvider";
import { useEffect, useState } from "react";
import InstructionIcon from "./InstructionIcon";
import { FormatDistance } from "../navcore/common/helpers";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      zIndex: 10,
      minHeight: "50px",
      width: "100%",
      background: "#000",
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      display: "flex",
      alignContent: "center"
    },
    rootMissingGPSFix: {
      background: theme.palette.error.dark
    },
    grid: {
      width: "100%",
      height: "100%",
      position: "relative"
    },
    instructionIcon: {
      color: "#FFF",
      width: "64px",
      height: "auto",
      filter: "invert(100%)"
    },
    topText: {
      color: "#FFF",
      position: "relative",
      height: "32px",
      width: "100%",
      fontWeight: 600,
      fontSize: "22px"
    },
    lowerText: {
      fontSize: "16px",
      opacity: 0.8,
      color: "#FFF",
      fontWeight: 500,
    },
    whiteText: {
      color: "#FFF",
    },
    progress: {
      color: "#FFF"
    },
    nextInstructionBox: {
      position: "absolute",
      bottom: theme.spacing(1),
      left: 0,
      height: "auto",
      minHeight: "2px",
      width: "100%",
      transition: "transform 0.25s, opacity 0.25s",
      transform: `translateY(0%)`,
      zIndex: -1,
      borderBottomLeftRadius: theme.spacing(1),
      borderBottomRightRadius: theme.spacing(1),
      paddingTop: theme.spacing(2),
      padding: theme.spacing(1),
      background: "#222",
      opacity: 0,
    },
    nextInstructionIn: {
      transform: "translateY(100%)",
      opacity: 1
    },
    nextInstructionIcon: {
      color: "#FFF",
      marginBottom: "-6px",
      width: "auto",
      height: "30px",
      filter: "invert(100%)",
      paddingTop: "4px",
      opacity: 0.8,
    },
    nextInstructionText: {
      color: "#FFF",
      position: "relative",
      marginTop: "4px",
      marginLeft: "8px",
      opacity: 0.8,
    }
  })
);

export default function TurnByTurnDisplay(props) {
  const { className } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { navCore } = useNavigation();
  const [instruction, setInstruction] = useState(null);
  const [nextInstruction, setNextInstruction] = useState(null);
  const [nextInstructionClose, setNextInstructionClose] = useState(false);
  const [hasGPSFix, setHasGPSFix] = useState(true);
  const [ready, setReady] = useState(false);

  const [metersToInstruction, setMetersToInstruction] = useState("0");

  useEffect(() => {
    if (!!navCore) {
      setHasGPSFix(navCore.geolocation.hasGPSFix());
      setReady(navCore.geolocation.gpsReady());


      // next instruction close
      let nc = false;

      // meters function
      const metersFn = (m, nextInstruction, timeToDestination) => {

        if (m < 1000) {
          setMetersToInstruction(m === 0 ? t("navigation:common:now") : `${m} m`);
        } else {
          setMetersToInstruction(`${FormatDistance(m / 1000.0)} km`);
        }

        const c = nextInstruction < 250;

        if (nc !== c) {
          nc = c;
          setNextInstructionClose(c);
        }
      }


      const instructionsFn = (instruction, nextInstruction) => {
        setInstruction(instruction);
        setNextInstruction(nextInstruction);
      };

      const gpsfixFn = (hasFix) => {
        setHasGPSFix(hasFix);
      };

      const gpsReadyFn = () => {
        setReady(true);
      };

      navCore.instructions.on("instruction", instructionsFn);
      navCore.instructions.on("metersToInstruction", metersFn);
      navCore.geolocation.on("gpsready", gpsReadyFn);
      navCore.geolocation.on("gpsfix", gpsfixFn);


      return () => {
        navCore.instructions.off("instruction", instructionsFn);
        navCore.instructions.off("metersToInstruction", metersFn);
        navCore.geolocation.off("gpsready", gpsReadyFn);
        navCore.geolocation.off("gpsfix", gpsfixFn);
      };
    }
  }, [navCore, t]);

  return (
    <Box className={`${classes.root} ${!hasGPSFix ? classes.rootMissingGPSFix : ""} ${className || ""}`}>

      {
        hasGPSFix && ready ? (
          <Grid container className={classes.grid} direction="row" alignItems="center" justifyContent="center">

            <Grid item xs={3}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item>
                  <InstructionIcon type={instruction?.type} modifier={instruction?.modifier} className={classes.instructionIcon} exit={instruction?.exit} isRoundabout={instruction?.isRoundabout} />
                </Grid>

              </Grid>
            </Grid>

            <Grid item xs>
              <Grid container direction="column">

                <Grid item xs={12}>
                  <Typography variant="body2" className={classes.topText}>{metersToInstruction === "0" ? t("navigation:common:now") : metersToInstruction}</Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body1" className={classes.lowerText}>{instruction?.name || t("navigation:common:waiting")}</Typography>
                </Grid>

              </Grid>
            </Grid>

          </Grid>
        ) : !ready ? (
          <Grid container className={classes.grid} direction="row" alignItems="center" justifyContent="center" spacing={2}>
            <Grid item>
              <Grid container justifyContent="center" alignItems="center">
                <CircularProgress variant="indeterminate" color="primary" size="32px" className={classes.progress} />
              </Grid>
            </Grid>
            <Grid item xs>
              <Typography className={classes.whiteText} variant="h5">{t("navigation:common:gpsLoading")}</Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container className={classes.grid} direction="row" alignItems="center" justifyContent="center">
            <Typography className={classes.whiteText} variant="h5">{t("navigation:common:noGPSFix")}</Typography>
          </Grid>
        )
      }

      <Box className={`${classes.nextInstructionBox} ${hasGPSFix && nextInstructionClose && !!nextInstruction ? classes.nextInstructionIn : ""}`}>

        <Grid container justifyContent="flex-start" alignItems="center" direction="row" spacing={1}>

          <Grid item>
            <Typography variant="body1" className={classes.nextInstructionText}>{t("navigation:common:nextUp")}</Typography>
          </Grid>

          <Grid item>
            <InstructionIcon type={nextInstruction?.type} modifier={nextInstruction?.modifier} className={classes.nextInstructionIcon} />
          </Grid>

        </Grid>

      </Box>
    </Box>
  )
}

